import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Receipt, Paperclip, Upload, Tag, X, ShoppingCart, CheckCircle, Package, Eye, FileDown, RefreshCw, BarChart3, Calendar } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import { formatCurrency, getCurrencySymbol, fmtNum } from '@/utils/currency';
import POViewer from '@/components/POViewer';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function Expenses() {
  const { token, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('expenses'); // 'expenses' or 'purchase-orders'
  const [expenses, setExpenses] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const [showSettleDialog, setShowSettleDialog] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [settlingExpense, setSettlingExpense] = useState(null);
  const [settlementData, setSettlementData] = useState({ bank_account: '', payee: '' });
  const [selectedBankName, setSelectedBankName] = useState('');
  const [viewingPO, setViewingPO] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [supplierSummary, setSupplierSummary] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const canSettle = user?.permissions?.includes('settle_expenses') || user?.permissions?.includes('all');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    details: '',
    amount: '',
    category_id: '',
    attachment: null,
    currency: 'USD',
    supplier_name: '',
    is_recurring: false,
    recurrence_interval: 'monthly'
  });
  const [poFormData, setPOFormData] = useState({
    supplier_name: '',
    date: new Date().toISOString().split('T')[0],
    items: [],
    category_id: '',
    notes: '',
    tax: 0,
    currency: 'USD'
  });
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  useEffect(() => {
    fetchExpenses();
    fetchPurchaseOrders();
    fetchCategories();
    fetchProducts();
    fetchExchangeRates();
    fetchBankAccounts();
    fetchSuppliers();
    fetchSupplierSummary();
  }, []);
  
  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseOrders(response.data);
    } catch (error) {
      toast.error('Failed to load purchase orders');
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };
  
  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Failed to load bank accounts');
    }
  };
  
  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  };

  const fetchSupplierSummary = async () => {
    try {
      const response = await axios.get(`${API}/expenses/supplier-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierSummary(response.data);
    } catch (error) {
      console.error('Failed to load supplier summary');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, attachment: reader.result });
      setAttachmentPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/expenses`, {
        date: new Date(formData.date).toISOString(),
        details: formData.details,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id,
        attachment: formData.attachment,
        currency: formData.currency,
        supplier_name: formData.supplier_name,
        is_recurring: formData.is_recurring,
        recurrence_interval: formData.is_recurring ? formData.recurrence_interval : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Expense recorded successfully');
      setShowExpenseDialog(false);
      resetForm();
      fetchExpenses();
      fetchSupplierSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    try {
      await axios.post(`${API}/expense-categories`, {
        name: newCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Category created successfully');
      setNewCategory('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await axios.delete(`${API}/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };
  
  const handleSettleExpense = async () => {
    if (!settlementData.bank_account || !settlementData.payee) {
      toast.error('Please select bank account and payee');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/expenses/${settlingExpense.id}/settle`, {
        bank_account: settlementData.bank_account,
        payee: settlementData.payee
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Expense settled successfully');
      setShowSettleDialog(false);
      setSettlingExpense(null);
      setSettlementData({ bank_account: '', payee: '' });
      setSelectedBankName('');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to settle expense');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      details: '',
      amount: '',
      category_id: '',
      attachment: null,
      currency: 'USD',
      supplier_name: '',
      is_recurring: false,
      recurrence_interval: 'monthly'
    });
    setAttachmentPreview(null);
  };


  // Purchase Order Functions
  const addPOItem = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      price: product.cost || 0,
      total: product.cost || 0
    };

    setPOFormData({
      ...poFormData,
      items: [...poFormData.items, newItem]
    });
  };

  const updatePOItem = (index, field, value) => {
    const updatedItems = [...poFormData.items];
    updatedItems[index][field] = parseFloat(value) || 0;
    
    if (field === 'quantity' || field === 'price') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].price;
    }
    
    setPOFormData({ ...poFormData, items: updatedItems });
  };

  const removePOItem = (index) => {
    setPOFormData({
      ...poFormData,
      items: poFormData.items.filter((_, i) => i !== index)
    });
  };

  const calculatePOTotals = () => {
    let subtotal = poFormData.items.reduce((sum, item) => sum + item.total, 0);
    
    // Convert to selected currency if not USD
    if (poFormData.currency !== 'USD') {
      const rate = exchangeRates.find(r => r.currency === poFormData.currency);
      if (rate) {
        subtotal = subtotal * rate.rate_to_usd;
      }
    }
    
    const taxAmount = subtotal * (poFormData.tax / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const getPOCurrencySymbol = () => {
    if (poFormData.currency === 'USD') return '$';
    if (poFormData.currency === 'MVR') return 'ރ';
    if (poFormData.currency === 'CHN') return '¥';
    return poFormData.currency + ' ';
  };

  const handleSubmitPO = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (poFormData.items.length === 0) {
      toast.error('Please add at least one item');
      setLoading(false);
      return;
    }

    if (!poFormData.category_id) {
      toast.error('Please select an expense category');
      setLoading(false);
      return;
    }

    try {
      const { subtotal, total } = calculatePOTotals();
      
      await axios.post(`${API}/purchase-orders`, {
        supplier_name: poFormData.supplier_name,
        date: new Date(poFormData.date).toISOString(),
        items: poFormData.items,
        subtotal,
        tax: poFormData.tax,
        total,
        category_id: poFormData.category_id,
        notes: poFormData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Purchase order created successfully');
      setShowPODialog(false);
      resetPOForm();
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  const completePO = async (poId) => {
    if (!window.confirm('Complete this purchase order and push to expenses? This will also update inventory.')) return;

    try {
      const response = await axios.put(`${API}/purchase-orders/${poId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`PO completed! Created expense: ${response.data.expense_id}`);
      fetchPurchaseOrders();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete purchase order');
    }
  };

  const deletePO = async (poId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;

    try {
      await axios.delete(`${API}/purchase-orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Purchase order deleted successfully');
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  const resetPOForm = () => {
    setPOFormData({
      supplier_name: '',
      date: new Date().toISOString().split('T')[0],
      items: [],
      category_id: '',
      notes: '',
      tax: 0,
      currency: 'USD'
    });
  };


  const totalExpensesFull = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalPOs = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  const pendingPOs = purchaseOrders.filter(po => po.status === 'pending').length;
  const PIE_COLORS = ['#059669', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#e11d48', '#65a30d'];

  const filteredExpenses = expenses.filter(exp => {
    if (filterCategory !== 'all' && exp.category_id !== filterCategory) return false;
    if (filterDateFrom) {
      const expDate = new Date(exp.date);
      if (expDate < new Date(filterDateFrom)) return false;
    }
    if (filterDateTo) {
      const expDate = new Date(exp.date);
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (expDate > toDate) return false;
    }
    return true;
  });
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const hasActiveFilters = filterCategory !== 'all' || filterDateFrom || filterDateTo;

  const handleExportPDF = () => {
    const rows = filteredExpenses.map((exp, i) => [
      i + 1,
      new Date(exp.date).toLocaleDateString(),
      exp.expense_id || '-',
      exp.category_name || '-',
      exp.details || '-',
      `$${fmtNum(exp.amount || 0)}`
    ]);
    exportListPDF({
      title: hasActiveFilters ? 'Expenses (Filtered)' : 'Expenses',
      headers: ['#', 'Date', 'Expense ID', 'Category', 'Details', 'Amount'],
      rows,
      totalLabel: 'Total Expenses',
      totalValue: `$${fmtNum(totalExpenses)}`,
      token, apiUrl: API,
    });
  };

  const convertToUsd = (amount, currency) => {
    if (currency === 'USD' || !currency) return amount;
    const rate = exchangeRates.find(r => r.currency === currency);
    return rate ? amount / rate.rate_to_usd : amount;
  };

  const handleExportMonthlyReport = async () => {
    const [year, month] = reportMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);

    // Fetch all expenses including settled for the report
    let allExpenses = [];
    try {
      const response = await axios.get(`${API}/expenses?include_settled=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      allExpenses = response.data;
    } catch {
      toast.error('Failed to fetch expenses for report');
      return;
    }

    // Filter by month
    const monthlyExpenses = allExpenses.filter(exp => {
      const d = new Date(exp.date);
      return d >= monthStart && d <= monthEnd;
    });

    if (monthlyExpenses.length === 0) {
      toast.error('No expenses found for the selected month');
      return;
    }

    // Group by category
    const grouped = {};
    monthlyExpenses.forEach(exp => {
      const cat = exp.category_name || 'Uncategorized';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(exp);
    });

    // Build PDF using jsPDF
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF();

    let logo = null;
    try {
      const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      logo = res.data?.company_logo;
    } catch {}

    const monthName = monthStart.toLocaleString('default', { month: 'long', year: 'numeric' });
    let y = 15;

    if (logo) {
      try {
        doc.addImage(logo, 'AUTO', 14, 10, 25, 25);
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 138);
        doc.text(`Expense Report — ${monthName}`, 44, 22);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 44, 29);
        y = 42;
      } catch {
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 138);
        doc.text(`Expense Report — ${monthName}`, 14, 20);
        y = 30;
      }
    } else {
      doc.setFontSize(16);
      doc.setTextColor(30, 58, 138);
      doc.text(`Expense Report — ${monthName}`, 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 27);
      y = 34;
    }

    let grandTotalUsd = 0;

    Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).forEach(([categoryName, catExpenses]) => {
      // Category header
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(16, 85, 52);
      doc.text(categoryName, 14, y);
      y += 2;

      const rows = catExpenses.map((exp, i) => {
        const cur = exp.currency || 'USD';
        const sym = cur === 'USD' ? '$' : cur === 'MVR' ? 'MVR ' : cur === 'CHN' ? '¥' : cur + ' ';
        return [
          i + 1,
          new Date(exp.date).toLocaleDateString(),
          exp.expense_id || '-',
          exp.supplier_name || '-',
          exp.details?.substring(0, 40) || '-',
          `${sym}${fmtNum(exp.amount)}`
        ];
      });

      autoTable(doc, {
        startY: y,
        head: [['#', 'Date', 'ID', 'Supplier', 'Details', 'Amount']],
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: [30, 58, 138], fontSize: 8 },
        styles: { fontSize: 7, cellPadding: 2 },
        margin: { left: 14 },
      });

      y = doc.lastAutoTable.finalY + 4;

      // Currency subtotals for this category
      const currencyTotals = {};
      catExpenses.forEach(exp => {
        const cur = exp.currency || 'USD';
        if (!currencyTotals[cur]) currencyTotals[cur] = 0;
        currencyTotals[cur] += exp.amount;
        grandTotalUsd += convertToUsd(exp.amount, cur);
      });

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0);
      const subtotalParts = Object.entries(currencyTotals).map(([cur, total]) => {
        const sym = cur === 'USD' ? '$' : cur === 'MVR' ? 'MVR ' : cur === 'CHN' ? '¥' : cur + ' ';
        return `${sym}${fmtNum(total)}`;
      });
      doc.text(`Subtotal: ${subtotalParts.join(' | ')}`, 14, y);
      y += 8;

      // Check if we need a new page
      if (y > 260) {
        doc.addPage();
        y = 20;
      }
    });

    // Grand total
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text(`Grand Total (USD): $${fmtNum(grandTotalUsd)}`, 14, y + 4);
    doc.text(`Total Expenses: ${monthlyExpenses.length}`, 14, y + 12);

    doc.save(`expense-report-${reportMonth}.pdf`);
    toast.success('Report downloaded!');
    setShowReportDialog(false);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Expenses & Purchases</h1>
            <p className="text-slate-600 mt-1">
              {activeTab === 'expenses' 
                ? `${hasActiveFilters ? 'Filtered' : 'Total Active'} Expenses: ${formatCurrency(totalExpenses)} | ${filteredExpenses.length}${hasActiveFilters ? ` of ${expenses.length}` : ''} recorded`
                : `Total POs: ${formatCurrency(totalPOs)} | ${pendingPOs} pending`
              }
            </p>
          </div>
          <div className="flex gap-3">
            {activeTab === 'expenses' && (
              <>
                <Button variant="outline" onClick={handleExportPDF} data-testid="expenses-export-pdf">
                  <FileDown className="w-4 h-4 mr-2" /> PDF
                </Button>
                <Button variant="outline" onClick={() => setShowReportDialog(true)} data-testid="expenses-monthly-report">
                  <BarChart3 className="w-4 h-4 mr-2" /> Monthly Report
                </Button>
                <a href="/expenses/settled">
                  <Button variant="outline">
                    <Eye className="w-4 h-4 mr-2" />
                    View Settled
                  </Button>
                </a>
              </>
            )}
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Tag className="w-4 h-4 mr-2" />
                  Manage Categories
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Expense Categories ({categories.length})</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="New category name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <Button onClick={handleCreateCategory}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"
                      >
                        <span className="font-medium">{category.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                    {categories.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-slate-500">No categories yet</p>
                        <p className="text-sm text-slate-400 mt-1">Create your first category above</p>
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showExpenseDialog} onOpenChange={(open) => {
              setShowExpenseDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record New Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitExpense} className="space-y-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>Category *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Details *</Label>
                    <textarea
                      className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md"
                      value={formData.details}
                      onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                      placeholder="Describe the expense..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Currency</Label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1"
                        data-testid="expense-currency-select"
                      >
                        <option value="USD">USD</option>
                        {exchangeRates.map(r => (
                          <option key={r.currency} value={r.currency}>{r.currency}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Amount ({formData.currency}) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        required
                      />
                      {formData.currency !== 'USD' && formData.amount && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          ≈ ${(parseFloat(formData.amount) / (exchangeRates.find(r => r.currency === formData.currency)?.rate_to_usd || 1))} USD
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Vendor / Supplier</Label>
                    <select
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1"
                      data-testid="expense-supplier-select"
                    >
                      <option value="">-- None --</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is-recurring"
                        checked={formData.is_recurring}
                        onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        data-testid="expense-recurring-toggle"
                      />
                      <Label htmlFor="is-recurring" className="cursor-pointer flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-slate-500" />
                        Recurring Expense
                      </Label>
                    </div>
                    {formData.is_recurring && (
                      <div>
                        <Label className="text-xs text-slate-600">Repeat Every</Label>
                        <select
                          value={formData.recurrence_interval}
                          onChange={(e) => setFormData({ ...formData, recurrence_interval: e.target.value })}
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1"
                          data-testid="expense-recurrence-interval"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Attachment (Optional)</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="expense-attachment"
                    />
                    <label htmlFor="expense-attachment">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => document.getElementById('expense-attachment').click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {attachmentPreview ? 'Change Attachment' : 'Upload Attachment'}
                      </Button>
                    </label>
                    {attachmentPreview && (
                      <div className="mt-2 relative">
                        <img
                          src={attachmentPreview}
                          alt="Attachment preview"
                          className="w-full h-32 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, attachment: null });
                            setAttachmentPreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {loading ? 'Recording...' : 'Record Expense'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {activeTab === 'purchase-orders' && (
              <Dialog open={showPODialog} onOpenChange={(open) => {
                setShowPODialog(open);
                if (!open) resetPOForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Purchase Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Purchase Order</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitPO} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Supplier Name *</Label>
                        <Input
                          value={poFormData.supplier_name}
                          onChange={(e) => setPOFormData({ ...poFormData, supplier_name: e.target.value })}
                          placeholder="Enter supplier name"
                          required
                        />
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          value={poFormData.date}
                          onChange={(e) => setPOFormData({ ...poFormData, date: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Expense Category *</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCategoryDialog(true)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          New Category
                        </Button>
                      </div>
                      {categories.length === 0 ? (
                        <div className="p-4 border rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                          <p className="font-semibold">No categories available</p>
                          <p className="mt-1">Please create a category first using the "New Category" button above.</p>
                        </div>
                      ) : (
                        <select
                          value={poFormData.category_id}
                          onChange={(e) => setPOFormData({ ...poFormData, category_id: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          required
                        >
                          <option value="">Select category</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Items *</Label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              addPOItem(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-64 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Add product</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} - ${product.cost || 0}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="border rounded-lg p-4 space-y-3">
                        {poFormData.items.map((item, index) => (
                          <div key={index} className="flex gap-3 items-end">
                            <div className="flex-1">
                              <Label className="text-xs">Product</Label>
                              <Input value={item.product_name} disabled className="text-sm" />
                            </div>
                            <div className="w-24">
                              <Label className="text-xs">Qty</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updatePOItem(index, 'quantity', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="w-32">
                              <Label className="text-xs">Price</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.price}
                                onChange={(e) => updatePOItem(index, 'price', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="w-32">
                              <Label className="text-xs">Total</Label>
                              <Input value={fmtNum(item.total)} disabled className="text-sm font-bold" />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removePOItem(index)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        ))}
                        {poFormData.items.length === 0 && (
                          <p className="text-center text-slate-500 text-sm py-4">No items added yet</p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 border-t pt-4">
                      <div>
                        <Label>Currency *</Label>
                        <select
                          value={poFormData.currency}
                          onChange={(e) => setPOFormData({ ...poFormData, currency: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="USD">USD ($)</option>
                          {exchangeRates.map((rate) => (
                            <option key={rate.currency} value={rate.currency}>
                              {rate.currency} ({rate.currency === 'MVR' ? 'ރ' : rate.currency === 'CHN' ? '¥' : rate.currency})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Tax (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={poFormData.tax}
                          onChange={(e) => setPOFormData({ ...poFormData, tax: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <div className="bg-slate-50 p-4 rounded space-y-2">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span className="font-semibold">{getPOCurrencySymbol()}{fmtNum(calculatePOTotals().subtotal)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Tax:</span>
                            <span className="font-semibold">{getPOCurrencySymbol()}{fmtNum(calculatePOTotals().taxAmount)}</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span>Total:</span>
                            <span className="text-emerald-600">{getPOCurrencySymbol()}{fmtNum(calculatePOTotals().total)}</span>
                          </div>
                          {poFormData.currency !== 'USD' && (
                            <div className="text-xs text-slate-500 text-right pt-1">
                              Original: ${(calculatePOTotals().total / exchangeRates.find(r => r.currency === poFormData.currency)?.rate_to_usd || 1)} USD
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <textarea
                        className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md"
                        value={poFormData.notes}
                        onChange={(e) => setPOFormData({ ...poFormData, notes: e.target.value })}
                        placeholder="Additional notes..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? 'Creating...' : 'Create Purchase Order'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'expenses'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Receipt className="w-4 h-4 inline mr-2" />
            Expenses
          </button>
          <button
            onClick={() => setActiveTab('purchase-orders')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'purchase-orders'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <ShoppingCart className="w-4 h-4 inline mr-2" />
            Purchase Orders {pendingPOs > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingPOs}</span>
            )}
          </button>
        </div>

        {/* Expenses List */}
        {activeTab === 'expenses' && (
          <div className="space-y-6">

          {/* Filter Bar */}
          <Card className="p-4" data-testid="expense-filters">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-slate-500 mb-1 block">Category</Label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="filter-category"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500 mb-1 block">From</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="filter-date-from"
                />
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500 mb-1 block">To</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="filter-date-to"
                />
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFilterCategory('all'); setFilterDateFrom(''); setFilterDateTo(''); }}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="filter-clear"
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </Card>
          {/* Supplier Summary Chart */}
          {supplierSummary.length > 0 && (
            <Card className="p-6" data-testid="supplier-summary-chart">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Supplier Expense Summary (USD)
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={supplierSummary.map(s => ({ name: s.supplier, value: s.total_usd }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, value }) => `${name}: $${value.toFixed(0)}`}
                      dataKey="value"
                    >
                      {supplierSummary.map((_, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `$${fmtNum(v)}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Expenses Grid */}
          <div className="grid gap-4">
          {filteredExpenses.map((expense) => (
            <Card key={expense.id} className="p-6 hover:shadow-lg transition-all">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Receipt className="w-5 h-5 text-slate-600" />
                    <h3 className="text-xl font-bold text-slate-900">{expense.expense_id}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                      {expense.category_name}
                    </span>
                    {expense.is_recurring && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-semibold flex items-center gap-1" data-testid="recurring-badge">
                        <RefreshCw className="w-3 h-3" />
                        {expense.recurrence_interval === 'weekly' ? 'Weekly' : 'Monthly'}
                      </span>
                    )}
                    {expense.parent_recurring_id && !expense.is_recurring && (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-semibold">
                        Auto-generated
                      </span>
                    )}
                  </div>
                  <p className="text-slate-700 mt-2">{expense.details}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span>Date: {new Date(expense.date).toLocaleDateString()}</span>
                    {expense.supplier_name && (
                      <span className="text-slate-600">Supplier: <span className="font-medium">{expense.supplier_name}</span></span>
                    )}
                    {expense.currency && expense.currency !== 'USD' && (
                      <span className="text-indigo-600 font-medium">{expense.currency}</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <p className="text-3xl font-bold text-red-600">{formatCurrency(expense.amount)}</p>
                  {expense.settled && (
                    <div className="text-sm text-green-600 space-y-1 text-right">
                      <p className="font-semibold">SETTLED</p>
                      <p>By: {expense.settled_by_name || expense.settled_by}</p>
                      <p>Bank: {expense.paid_from_bank_name ? `${expense.paid_from_bank_name} — ` : ''}{expense.paid_from_bank}</p>
                      {expense.paid_from_currency && expense.paid_from_currency !== (expense.currency || 'USD') && (
                        <p className="text-indigo-600 text-xs font-medium">
                          Debited: {expense.paid_from_currency} {fmtNum(expense.debit_amount || 0)}
                        </p>
                      )}
                      <p>Payee: {expense.payee}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {expense.attachment && (
                      <Button
                        onClick={() => {
                          setSelectedAttachment(expense.attachment);
                          setShowAttachment(true);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        <Paperclip className="w-4 h-4" />
                      </Button>
                    )}
                    {!expense.settled && canSettle && (
                      <Button
                        onClick={() => {
                          setSettlingExpense(expense);
                          setShowSettleDialog(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Settle
                      </Button>
                    )}
                    {!expense.settled && (
                      <Button
                        onClick={() => handleDeleteExpense(expense.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {filteredExpenses.length === 0 && (
            <Card className="p-12 text-center">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              {hasActiveFilters ? (
                <>
                  <p className="text-slate-600 text-lg">No expenses match your filters</p>
                  <p className="text-slate-500 text-sm mt-2">Try adjusting the date range or category</p>
                </>
              ) : (
                <>
                  <p className="text-slate-600 text-lg">No expenses recorded yet</p>
                  <p className="text-slate-500 text-sm mt-2">Click "Add Expense" to record your first expense</p>
                </>
              )}
            </Card>
          )}

          {/* Settled Expenses Section */}
          </div>
          </div>
        )}

        {/* Purchase Orders List */}
        {activeTab === 'purchase-orders' && (
          <div className="grid gap-4">
            {purchaseOrders.map((po) => (
              <Card key={po.id} className="p-6 hover:shadow-lg transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <ShoppingCart className="w-5 h-5 text-slate-600" />
                      <h3 className="text-xl font-bold text-slate-900">{po.po_number}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        po.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        po.status === 'completed' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {po.status.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        {po.category_name}
                      </span>
                    </div>
                    <p className="text-slate-700 font-semibold">Supplier: {po.supplier_name}</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Date: {new Date(po.date).toLocaleDateString()} | {po.items.length} items
                    </p>
                    {po.notes && <p className="text-sm text-slate-600 mt-1">Notes: {po.notes}</p>}
                    {po.pushed_to_expense && (
                      <p className="text-sm text-emerald-600 mt-2 font-semibold">
                        ✓ Pushed to Expenses: {po.expense_id}
                      </p>
                    )}
                    
                    {/* Items list */}
                    <div className="mt-3 space-y-1">
                      {po.items.map((item, idx) => (
                        <div key={idx} className="text-sm text-slate-600 flex justify-between">
                          <span>• {item.product_name} x {item.quantity}</span>
                          <span className="font-mono">${fmtNum(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <p className="text-3xl font-bold text-blue-600">{formatCurrency(po.total)}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setViewingPO(po)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {po.status === 'pending' && !po.pushed_to_expense && (
                        <Button
                          onClick={() => completePO(po.id)}
                          variant="outline"
                          size="sm"
                          className="text-emerald-600 hover:bg-emerald-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      {!po.pushed_to_expense && (
                        <Button
                          onClick={() => deletePO(po.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {purchaseOrders.length === 0 && (
              <Card className="p-12 text-center">
                <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg">No purchase orders yet</p>
                <p className="text-slate-500 text-sm mt-2">Click "Create Purchase Order" to get started</p>
              </Card>
            )}
          </div>
        )}

        {/* Attachment Viewer Dialog */}
        <Dialog open={showAttachment} onOpenChange={setShowAttachment}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Expense Attachment</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center">
              {selectedAttachment && (
                <img
                  src={selectedAttachment}
                  alt="Expense Attachment"
                  className="max-w-full max-h-[70vh] object-contain rounded border"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* PO Viewer */}
        {viewingPO && (
          <POViewer po={viewingPO} onClose={() => setViewingPO(null)} />
        )}
        
        {/* Monthly Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Monthly Expense Report
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Month</Label>
                <Input
                  type="month"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  data-testid="report-month-picker"
                  className="mt-1"
                />
              </div>
              <p className="text-sm text-slate-500">
                Downloads a PDF report of all expenses for the selected month, grouped by category with per-currency subtotals and a USD grand total.
              </p>
              <Button
                onClick={handleExportMonthlyReport}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="download-monthly-report"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Settle Expense Dialog */}
        <Dialog open={showSettleDialog} onOpenChange={setShowSettleDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Settle Expense</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {settlingExpense && (
                <div className="bg-slate-50 p-4 rounded">
                  <p className="font-semibold">{settlingExpense.expense_id}</p>
                  <p className="text-sm text-slate-600">{settlingExpense.details}</p>
                  <p className="text-xl font-bold text-red-600 mt-2">
                    {settlingExpense.currency || 'USD'} {settlingExpense.amount}
                  </p>
                </div>
              )}
              
              <div>
                <Label>Select Bank *</Label>
                <select
                  value={selectedBankName}
                  onChange={(e) => {
                    setSelectedBankName(e.target.value);
                    setSettlementData({ ...settlementData, bank_account: '' });
                  }}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="">-- Select Bank --</option>
                  {[...new Set(bankAccounts.map(acc => acc.bank_name))].map((bankName) => (
                    <option key={bankName} value={bankName}>
                      {bankName}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedBankName && (
                <div>
                  <Label>Select Bank Account *</Label>
                  <div className="space-y-2 mt-2">
                    {bankAccounts
                      .filter(acc => acc.bank_name === selectedBankName)
                      .map((account) => {
                        const expCur = settlingExpense?.currency || 'USD';
                        const accCur = account.currency;
                        const isCrossCurrency = accCur !== expCur;
                        const rate = exchangeRates.find(r => r.currency === accCur);
                        const expRate = exchangeRates.find(r => r.currency === expCur);
                        let debitAmount = settlingExpense?.amount || 0;
                        if (isCrossCurrency) {
                          const expUsd = expCur === 'USD' ? debitAmount : debitAmount / (expRate?.rate_to_usd || 1);
                          debitAmount = accCur === 'USD' ? expUsd : expUsd * (rate?.rate_to_usd || 1);
                        }
                        const isSelected = settlementData.bank_account === (account.account_number || account.account_name || 'Main Account');
                        return (
                          <div
                            key={account.id}
                            onClick={() => setSettlementData({ ...settlementData, bank_account: account.account_number || account.account_name || 'Main Account' })}
                            className={`p-3 border rounded cursor-pointer hover:border-blue-500 ${
                              isSelected ? 'border-blue-500 bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold">{account.account_number || account.account_name || 'Main Account'}</p>
                                <p className="text-sm text-slate-600">{account.bank_name} ({accCur})</p>
                                {isCrossCurrency && isSelected && (
                                  <p className="text-xs text-indigo-600 mt-1">
                                    Will debit {accCur} {fmtNum(debitAmount)} for {expCur} {settlingExpense?.amount} expense
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-bold">{accCur} {account.balance.toLocaleString()}</p>
                                <p className="text-xs text-slate-500">Balance</p>
                                {account.balance < debitAmount && (
                                  <p className="text-xs text-red-500">Insufficient</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              
              {!selectedBankName && bankAccounts.length === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-center">
                  <p className="text-sm text-yellow-800">
                    No bank accounts found.
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Please add a bank account in Accounting first.
                  </p>
                </div>
              )}
              
              <div>
                <Label>Select or Enter Payee *</Label>
                <select
                  value={settlementData.payee}
                  onChange={(e) => setSettlementData({ ...settlementData, payee: e.target.value })}
                  className="w-full border rounded px-3 py-2 mb-2"
                >
                  <option value="">-- Select Supplier or Type Below --</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Or type payee name"
                  value={settlementData.payee}
                  onChange={(e) => setSettlementData({ ...settlementData, payee: e.target.value })}
                />
              </div>
              
              <Button
                onClick={handleSettleExpense}
                disabled={!settlementData.bank_account || !settlementData.payee || loading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Settling...' : 'Confirm Settlement'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
